/**
 * OfflineBanner Component
 *
 * Displays a non-intrusive banner when the user is offline.
 * Uses ConfigurationService for message and dismissibility settings.
 * Integrates with i18n for translations.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import ConfigurationService from '../../services/ConfigurationService';

export interface OfflineBannerProps {
    /** Custom class name for styling */
    className?: string;
    /** Override the default offline message */
    message?: string;
}

/**
 * Banner component that appears when the user loses network connectivity.
 *
 * Features:
 * - Auto-shows when offline, auto-hides when online
 * - Dismissible based on configuration
 * - i18n translated
 * - Accessible with ARIA attributes
 */
export const OfflineBanner: React.FC<OfflineBannerProps> = ({ className = '', message }) => {
    const { t } = useTranslation();
    const { isOnline, wasOffline } = useNetworkStatus();
    const [isDismissed, setIsDismissed] = useState(false);
    const [showReconnected, setShowReconnected] = useState(false);

    const isDismissible = ConfigurationService.isOfflineBannerDismissible();
    const defaultMessage = ConfigurationService.getOfflineMessage();

    // Reset dismissed state when going offline again
    useEffect(() => {
        if (!isOnline) {
            setIsDismissed(false);
        }
    }, [isOnline]);

    // Show reconnected message briefly (configurable timeout)
    useEffect(() => {
        if (wasOffline && isOnline) {
            setShowReconnected(true);
            const timeout = ConfigurationService.getReconnectedBannerTimeout();
            const timer = setTimeout(() => {
                setShowReconnected(false);
            }, timeout);
            return () => clearTimeout(timer);
        }
    }, [wasOffline, isOnline]);

    const handleDismiss = () => {
        if (isDismissible) {
            setIsDismissed(true);
        }
    };

    // Show reconnected banner
    if (showReconnected) {
        return (
            <div
                role="status"
                aria-live="polite"
                className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 
                    bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg 
                    flex items-center gap-3 z-50 animate-slide-up ${className}`}
            >
                <WifiIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span className="text-sm font-medium">
                    {t('offline.reconnected', 'Connection restored!')}
                </span>
            </div>
        );
    }

    // Don't show if online or dismissed
    if (isOnline || isDismissed) {
        return null;
    }

    return (
        <div
            role="alert"
            aria-live="assertive"
            className={`fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 
                bg-yellow-500 text-yellow-900 px-4 py-3 rounded-lg shadow-lg 
                flex items-center gap-3 z-50 animate-slide-up ${className}`}
        >
            <WifiIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <span className="text-sm font-medium flex-1">
                {message || t('offline.message', defaultMessage)}
            </span>
            {isDismissible && (
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="p-1 hover:bg-yellow-600/20 rounded transition-colors"
                    aria-label={t('common.dismiss', 'Dismiss')}
                >
                    <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
            )}
        </div>
    );
};

export default OfflineBanner;
